import { makeAutoObservable, runInAction, reaction } from 'mobx';
import bcrypt from 'bcryptjs';
import {
  setNativeKioskLocked,
  sendKioskHeartbeat,
} from '~/services/platform/kioskMode';

/**
 * Kiosk Store
 *
 * Manages the lockdown state of the player application.
 * Lockdown prevents user interaction with the media content.
 *
 * Fase 3 do KIOSK_PASSWORD_PLAN: adiciona suporte a PIN de desbloqueio,
 * lockout progressivo persistido e fila offline de auditoria.
 *
 * Back-compat: se {@link unlockPinHashes} estiver vazio, o 7-click desbloqueia
 * direto (comportamento anterior à feature). PINs cadastrados ativam o teclado.
 */

const STORAGE_KEY_FAIL_COUNT = 'efflux_kiosk_fail_count';
const STORAGE_KEY_LOCKED_UNTIL = 'efflux_kiosk_locked_until';
const STORAGE_KEY_AUDIT_QUEUE = 'efflux_kiosk_audit_queue';
// 2026-05-24: serverEnabled/unlockPinHashes persistidos pra sobreviver a reboot
// offline. Sem isso, no boot com server inacessível o JS começa serverEnabled=false
// → isLocked=false → SecretArea do KioskGuard NÃO renderiza → operador em loja
// fica sem como acionar o 7-click pra desbloquear o tablet (que pode estar
// fisicamente em Lock Task mode no Android). Persistir o último estado conhecido
// garante que a UI de unlock está sempre disponível enquanto o kiosk estiver
// ativo, mesmo nas telas de splash/sync/offline ANTES do primeiro sync.
const STORAGE_KEY_SERVER_ENABLED = 'efflux_kiosk_server_enabled';
const STORAGE_KEY_PIN_HASHES = 'efflux_kiosk_pin_hashes';

const FIRST_LOCKOUT_THRESHOLD = 10;
const SECOND_LOCKOUT_THRESHOLD = 20;
const FIRST_LOCKOUT_DURATION_MS = 30 * 1000;
const RECURRING_LOCKOUT_DURATION_MS = 5 * 60 * 1000;

/**
 * 2026-06-01 — Anti-brick recovery: se o player ficar locked + sem sync OK
 * com servidor por mais de 60min, libera automaticamente. Cobre o cenário
 * "TV instalada em loja sem rede / roteador queimado / controle remoto
 * perdido" — sem essa válvula, equipe de campo precisaria desinstalar o
 * WGT via Tizen Studio. Decisão Renato 2026-06-01 (Sprint K1).
 *
 * <p>O check roda a cada 60s enquanto isLocked. Quando voltar online +
 * receber sync OK, o admin/sync pode re-trancar via oscilar serverEnabled.</p>
 */
const OFFLINE_AUTO_UNLOCK_THRESHOLD_MS = 60 * 60 * 1000; // 60min
const OFFLINE_AUTO_UNLOCK_CHECK_INTERVAL_MS = 60 * 1000; // 60s

/**
 * P6: intervalo do heartbeat pro freeze watchdog nativo. 30s é folgado o
 * suficiente pra não onerar a bridge JS↔Native e curto o bastante pro
 * watchdog (threshold 10min) ter resolução suficiente.
 */
const HEARTBEAT_INTERVAL_MS = 30 * 1000;

export interface PendingUnlockAuditEvent {
  /** UUID gerado no cliente pra dedup no servidor. */
  clientEventId: string;
  /** Epoch ms do evento. */
  timestamp: number;
  success: boolean;
  lockoutTriggered: boolean;
  lockoutDurationSec: number;
}

export interface UnlockAttemptResult {
  success: boolean;
  /** Se a tentativa disparou um lockout (ou estava em lockout antes). */
  locked: boolean;
  /** Tempo restante de lockout em segundos (0 se livre). */
  remainingLockoutSec: number;
}

const safeReadNumber = (key: string): number => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
};

const safeReadNullableNumber = (key: string): number | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
};

const safeReadQueue = (): PendingUnlockAuditEvent[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_AUDIT_QUEUE);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const safeReadBoolean = (key: string): boolean => {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

const safeReadStringArray = (key: string): string[] => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((h): h is string => typeof h === 'string' && h.length > 0)
      : [];
  } catch {
    return [];
  }
};

const safeWrite = (key: string, value: string | null) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // localStorage indisponível (modo privado, quotas) — segue sem persistir
  }
};

const generateClientEventId = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // Fallback simples; ainda único o suficiente pra dedup
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

class KioskStore {
  /** Kiosk mode habilitado pelo servidor (config sync). */
  serverEnabled = false;

  /**
   * v1.0.148 — device registrado mas PENDENTE de aprovação no admin.
   * Quando true, o kiosk trava defensivamente (cliente não mexe na janela de
   * espera "Aguardando aprovação"), mesmo antes do servidor enviar a config.
   * Setado por uma reaction em stores/index.ts sobre authStore.registrationStatus.
   * Operador sai com 7 cliques (sem PIN — ainda não há PIN configurado).
   */
  pendingApproval = false;

  /**
   * 2026-05-31 (v1.0.104) — Plataforma detectada (android/tizen/webos/browser),
   * setada via {@link setPlatform} pelo App.tsx após o usePlatform hook
   * resolver. **Observable** propositalmente — antes o getter {@link isLocked}
   * lia de `window.__effluxPlatform` que NÃO é observable: na primeira render
   * do KioskGuard (constructor singleton roda antes do React commit), a flag
   * window ainda era undefined → fallback 'browser' → isLocked retornava
   * false → KioskOverlay não renderizava. Quando admin oscilava OFF→ON,
   * setServerEnabled mudava o observable e finalmente invalidava o computed,
   * dando "vida" ao isLocked. Bug se manifestava como "kiosk 50% locked no
   * boot" (LockTask nativo OK pela reaction com fireImmediately, mas React
   * sem KioskOverlay).
   */
  platform: string = 'browser';

  /** Hashes bcrypt ativos pra desbloqueio (combina PINs do tenant + globais). */
  unlockPinHashes: string[] = [];

  /** Bypass session-only após desbloqueio bem-sucedido. */
  private manuallyUnlocked = false;

  /** Persistido — total de falhas desde o último sucesso. */
  failCount = 0;

  /** Persistido — epoch ms até quando o teclado fica bloqueado. */
  lockedUntil: number | null = null;

  /** Fila de eventos de auditoria que ainda não foram pro servidor. */
  auditQueue: PendingUnlockAuditEvent[] = [];

  /** Disposer da reaction de sync nativo (apenas após {@link initNativeBridge}). */
  private nativeBridgeDisposer: (() => void) | null = null;

  /**
   * P6 (deep-review #15): freeze watchdog heartbeat. Quando kiosk está
   * ativo, manda heartbeat pro plugin nativo a cada {@link HEARTBEAT_INTERVAL_MS}.
   * Native watchdog usa isso pra detectar freeze do WebView e auto-recovery.
   */
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * 2026-06-01 — Anti-brick watchdog. Roda enquanto isLocked, checa
   * periodicamente se o player perdeu contato com server por > 60min e
   * libera o kiosk pra equipe de campo conseguir acessar fisicamente.
   */
  private offlineWatchdogTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    // Hidrata estado persistido antes de marcar observable
    this.failCount = safeReadNumber(STORAGE_KEY_FAIL_COUNT);
    this.lockedUntil = safeReadNullableNumber(STORAGE_KEY_LOCKED_UNTIL);
    this.auditQueue = safeReadQueue();
    // 2026-05-24: hidrata último estado conhecido de kiosk pra que o
    // KioskGuard renderize a SecretArea (7-click no canto sup. esquerdo)
    // já no boot, ANTES do primeiro sync. Sem isso, tablet com server
    // inacessível ficava sem caminho de desbloqueio nas telas de
    // splash/no-internet/server-unreachable. Vide STORAGE_KEY_SERVER_ENABLED.
    this.serverEnabled = safeReadBoolean(STORAGE_KEY_SERVER_ENABLED);
    this.unlockPinHashes = safeReadStringArray(STORAGE_KEY_PIN_HASHES);
    makeAutoObservable(this);
    // OBS: A reaction que sincroniza com o plugin nativo Android NÃO é instalada
    // aqui porque ela mantém um observer permanente sobre o computed `isLocked`,
    // o que faz mobx cachear o valor — mas o computed depende também de
    // {@code window.__effluxPlatform}, que não é observable. Em testes isso causa
    // staleness. A reaction é instalada via {@link initNativeBridge} no boot do app.
  }

  /**
   * KIOSK_PASSWORD_PLAN Fase 3 / Nível 1: instala a reaction que sincroniza
   * {@link isLocked} com o plugin nativo Android (consumir volume/back keys).
   *
   * <p>Chamado uma vez no boot da aplicação (App.tsx). No-op idempotente se
   * chamado duas vezes. Em testes não é chamado — então o computed `isLocked`
   * permanece sem observers e re-avalia a cada leitura, evitando cache stale.
   */
  initNativeBridge(): void {
    if (this.nativeBridgeDisposer) return;
    this.nativeBridgeDisposer = reaction(
      () => this.isLocked,
      (locked) => {
        setNativeKioskLocked(locked).catch(() => {
          /* silent: bridge issues não devem quebrar UX */
        });
        // P6: liga/desliga heartbeat conforme estado do kiosk. Sem kiosk,
        // não há razão pra heartbeat (e watchdog não checa).
        if (locked) {
          this.startHeartbeat();
          this.startOfflineWatchdog();
        } else {
          this.stopHeartbeat();
          this.stopOfflineWatchdog();
        }
      },
      { fireImmediately: true },
    );

    // 2026-05-31 (v1.0.102) — Boot race fix.
    //
    // Renato observou: tablet sobe com kiosk admin=ON, mas barra/painelzinho
    // ficam acessíveis até admin desligar+religar (oscilação força reaction
    // a re-disparar). Causa raiz: a reaction com fireImmediately roda ANTES
    // do Capacitor terminar de registrar o KioskMode plugin nativo, então
    // `Capacitor.isPluginAvailable('KioskMode')` retorna false e o
    // setNativeKioskLocked vira no-op silencioso. O React (KioskOverlay)
    // renderiza, mas nativo não entra em LockTask → swipe da barra passa.
    //
    // Fix: re-disparar setLocked com delay grande o suficiente pro plugin
    // estar pronto. Idempotente — se já trancou no boot, não muda nada.
    setTimeout(() => {
      const locked = this.isLocked;
      console.log(
        `[KioskStore] 🔁 Post-boot native bridge resync — isLocked=${locked}`,
      );
      setNativeKioskLocked(locked).catch(() => {
        /* silent */
      });
    }, 1500);
  }

  /** Desliga a reaction nativa (útil em hot-reload e testes). */
  disposeNativeBridge(): void {
    if (this.nativeBridgeDisposer) {
      this.nativeBridgeDisposer();
      this.nativeBridgeDisposer = null;
    }
    // P6: garante que o timer não fique solto após disposição
    this.stopHeartbeat();
  }

  /**
   * P6: inicia loop de heartbeat pro freeze watchdog nativo. Idempotente.
   * Disparado automaticamente quando isLocked vira true (via reaction).
   */
  private startHeartbeat(): void {
    if (this.heartbeatTimer !== null) return;
    // Dispara um heartbeat imediato + agenda os próximos
    sendKioskHeartbeat().catch(() => {
      /* silent */
    });
    this.heartbeatTimer = setInterval(() => {
      sendKioskHeartbeat().catch(() => {
        /* silent: heartbeat é best-effort */
      });
    }, HEARTBEAT_INTERVAL_MS);
  }

  /**
   * 2026-06-01 (v1.0.105) — Anti-brick watchdog. Roda enquanto isLocked,
   * checa a cada 60s se o player perdeu contato com servidor (lastSyncTime
   * antigo demais). Sem internet por > 60min em modo kiosk = auto-unlock
   * pra equipe de campo conseguir acessar fisicamente. Decisão Renato
   * 2026-06-01.
   *
   * <p>Idempotente. Disparado pela reaction quando isLocked vira true.</p>
   */
  private startOfflineWatchdog(): void {
    if (this.offlineWatchdogTimer !== null) return;
    this.offlineWatchdogTimer = setInterval(() => {
      this.checkOfflineAutoUnlock();
    }, OFFLINE_AUTO_UNLOCK_CHECK_INTERVAL_MS);
  }

  private stopOfflineWatchdog(): void {
    if (this.offlineWatchdogTimer !== null) {
      clearInterval(this.offlineWatchdogTimer);
      this.offlineWatchdogTimer = null;
    }
  }

  /**
   * Checa se deve auto-unlock por timeout offline. Import dinâmico do
   * syncStore evita dep cíclica e mantém testes do kioskStore isolados.
   */
  private async checkOfflineAutoUnlock(): Promise<void> {
    if (!this.isLocked) return;
    try {
      const { syncStore } = await import('~/stores');
      const lastSyncStr = syncStore.lastSyncTime;
      if (!lastSyncStr) return; // nunca sincronizou — sem baseline
      const lastSyncMs = new Date(lastSyncStr).getTime();
      if (!Number.isFinite(lastSyncMs)) return;
      const elapsed = Date.now() - lastSyncMs;
      if (elapsed < OFFLINE_AUTO_UNLOCK_THRESHOLD_MS) return;
      console.warn(
        `[KioskStore] ⚠️ Auto-unlock por offline > 60min (last sync: ${lastSyncStr}, elapsed: ${Math.round(elapsed / 60_000)}min). Liberando kiosk pra recovery de campo.`,
      );
      runInAction(() => {
        this.manuallyUnlocked = true;
      });
      this.enqueueAuditEvent({
        success: true,
        lockoutTriggered: false,
        lockoutDurationSec: 0,
      });
      this.stopOfflineWatchdog();
    } catch (err) {
      console.warn('[KioskStore] checkOfflineAutoUnlock erro:', err);
    }
  }

  /** P6: para o loop de heartbeat (kiosk desativado ou disposer chamado). */
  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // =========================================================================
  // Computed
  // =========================================================================

  /** Pelo menos um PIN ativo pra esse player (tenant + globais). */
  get requiresPinForUnlock(): boolean {
    return this.unlockPinHashes.length > 0;
  }

  /** Tempo restante de lockout em segundos (0 se livre). */
  get remainingLockoutSec(): number {
    if (!this.lockedUntil) return 0;
    const remaining = Math.ceil((this.lockedUntil - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  }

  /**
   * Player deve estar locked? Aplica regras:
   * 1. Só locks se serverEnabled.
   * 2. Não locks se manualmente desbloqueado nesta sessão.
   *
   * 2026-06-08 — Browser passou a respeitar serverEnabled (antes tinha
   * bypass hardcoded). Decisão Renato: kiosk em browser = best-effort
   * cosmético (KioskOverlay bloqueia clique DENTRO do canvas; cursor
   * visível pra 7-click ser óbvio). Dev local escapa via Alt+Tab/
   * Cmd+W/F12 sem precisar de bypass — o lock é só DENTRO da aba.
   */
  get isLocked(): boolean {
    // 7-click/PIN destrava ESTA sessão — vale tanto pro kiosk normal quanto
    // pro lock defensivo de pending (operador sempre consegue sair).
    if (this.manuallyUnlocked) return false;
    // v1.0.148 — device registrado mas pendente de aprovação: trava
    // defensivamente (cliente não mexe em "Aguardando aprovação"). Quando
    // aprovado, pendingApproval=false e o serverEnabled passa a mandar.
    if (this.pendingApproval) return true;
    return this.serverEnabled;
  }

  /** v1.0.148 — setado pela reaction sobre authStore.registrationStatus. */
  setPendingApproval(pending: boolean): void {
    if (this.pendingApproval === pending) return;
    console.log(
      `[KioskStore] pendingApproval: ${this.pendingApproval} -> ${pending}`,
    );
    this.pendingApproval = pending;
  }

  /** Public read-only — útil pra UI saber se mostrar botão "Reactivate Kiosk". */
  get isServerEnabled(): boolean {
    return this.serverEnabled;
  }

  // =========================================================================
  // Server-driven setters
  // =========================================================================

  /**
   * 2026-05-31 (v1.0.104) — Setado pelo App.tsx no useEffect do usePlatform
   * hook. Invalidar o computed {@link isLocked} sempre que a plataforma fica
   * conhecida — sem isso, o KioskGuard renderiza com `isLocked=false` no boot
   * (constructor singleton roda antes do React) e fica stale até admin
   * oscilar serverEnabled. Idempotente.
   */
  setPlatform(platform: string): void {
    if (this.platform === platform) return;
    console.log(`[KioskStore] Platform: ${this.platform} -> ${platform}`);
    this.platform = platform;
  }

  setServerEnabled(enabled: boolean): void {
    if (this.serverEnabled === enabled) {
      // 2026-05-31 (v1.0.102) — Mesmo quando o valor não muda, re-sincroniza
      // o plugin nativo. Cobre o caso: tablet sobe → reaction MobX dispara
      // setLocked(true) MAS Capacitor plugin ainda não está pronto → vira
      // no-op silencioso. Sync chega confirmando o mesmo valor → setter
      // antigamente retornava early sem dar segunda chance. Agora chama
      // direto. Idempotente no nativo.
      import('~/services/platform/kioskMode')
        .then(({ setNativeKioskLocked }) => setNativeKioskLocked(this.isLocked))
        .catch(() => {
          /* silent */
        });
      return;
    }
    console.log(
      `[KioskStore] Server enabled: ${this.serverEnabled} -> ${enabled}`,
    );
    this.serverEnabled = enabled;
    safeWrite(STORAGE_KEY_SERVER_ENABLED, enabled ? 'true' : 'false');
    // Servidor desligou kiosk → libera bypass pra próxima reativação
    if (!enabled) this.manuallyUnlocked = false;
  }

  setUnlockPinHashes(hashes: string[] | undefined): void {
    const next = Array.isArray(hashes)
      ? hashes.filter((h) => typeof h === 'string' && h.length > 0)
      : [];
    // Evita re-render se nada mudou
    if (
      next.length === this.unlockPinHashes.length &&
      next.every((h, i) => h === this.unlockPinHashes[i])
    ) {
      return;
    }
    console.log(
      `[KioskStore] Unlock PIN hashes updated: ${this.unlockPinHashes.length} -> ${next.length}`,
    );
    this.unlockPinHashes = next;
    safeWrite(STORAGE_KEY_PIN_HASHES, JSON.stringify(next));
  }

  // =========================================================================
  // Unlock flow
  // =========================================================================

  /**
   * Desbloqueio direto (back-compat). Chamado pelo 7-click handler quando
   * NÃO há PIN cadastrado. Se há PIN, use {@link attemptUnlockWithPin}.
   */
  unlock(): void {
    if (!this.isLocked) return;
    if (this.requiresPinForUnlock) {
      console.warn(
        '[KioskStore] unlock() called but PIN required — use attemptUnlockWithPin',
      );
      return;
    }
    console.log(
      '[KioskStore] 🔓 Manual unlock (no PIN configured — back-compat)',
    );
    this.manuallyUnlocked = true;
    this.resetLockoutState();
  }

  /**
   * Tenta desbloquear com PIN. Valida bcrypt offline contra a lista de hashes,
   * aplica lockout progressivo e enfileira evento de auditoria.
   */
  async attemptUnlockWithPin(plainPin: string): Promise<UnlockAttemptResult> {
    // Em lockout → rejeita sem validar (não vaza info)
    if (this.remainingLockoutSec > 0) {
      return {
        success: false,
        locked: true,
        remainingLockoutSec: this.remainingLockoutSec,
      };
    }

    const isValid = await this.validatePinAgainstHashes(plainPin);

    if (isValid) {
      runInAction(() => {
        this.manuallyUnlocked = true;
        this.resetLockoutState();
      });
      this.enqueueAuditEvent({
        success: true,
        lockoutTriggered: false,
        lockoutDurationSec: 0,
      });
      return { success: true, locked: false, remainingLockoutSec: 0 };
    }

    // Falha — incrementa e checa lockout
    const lockoutDurationMs = this.applyFailureAndComputeLockout();
    const lockoutTriggered = lockoutDurationMs > 0;
    const remainingLockoutSec = lockoutTriggered
      ? Math.ceil(lockoutDurationMs / 1000)
      : 0;

    this.enqueueAuditEvent({
      success: false,
      lockoutTriggered,
      lockoutDurationSec: remainingLockoutSec,
    });

    return { success: false, locked: lockoutTriggered, remainingLockoutSec };
  }

  /**
   * Re-ativa o kiosk lockdown (chamado de Control Panel / Debug Panel).
   *
   * <p>Renato observou em prod (2026-05-31): botão "🔒 Reativar Kiosk" no painel
   * debug do tablet não estava funcionando. Causa raiz: a única coisa que
   * essa função fazia era setar {@code manuallyUnlocked = false} e confiar
   * 100% na reaction MobX pra propagar pro plugin nativo. Se a reaction não
   * disparasse (cache stale, observer não-instalado, race com setServerEnabled),
   * o plugin nativo ficava sem saber → teclas físicas continuavam funcionando.</p>
   *
   * <p>Fix: chamada DIRETA ao {@code setNativeKioskLocked(true)} além do
   * manuallyUnlocked reset. Redundante com a reaction, mas garante que o
   * native side recebe o sinal mesmo se MobX falhar. Idempotente — chamar
   * 2x não causa problema.</p>
   */
  lock(): void {
    console.log(
      '[KioskStore] 🔒 Manual re-lock — serverEnabled:',
      this.serverEnabled,
      'isLocked-before:',
      this.isLocked,
    );

    // 2026-05-31 (v1.0.101) — Se admin desabilitou kiosk remotamente
    // (kioskModeEnabled=false → serverEnabled=false), operador em loja ainda
    // pode querer trancar localmente (ex: equipe de campo terminou
    // manutenção, quer deixar o tablet seguro antes de sair sem mexer no
    // admin). Forçamos serverEnabled=true aqui — próximo sync pode reverter
    // pra false, mas até lá o operador tem proteção imediata.
    if (!this.serverEnabled) {
      console.warn(
        '[KioskStore] ⚠️ serverEnabled=false → forçando true localmente. Próximo sync pode reverter.',
      );
      this.setServerEnabled(true);
    }

    this.manuallyUnlocked = false;
    // Dispara native bridge DIRETO (não depender só da reaction MobX).
    // Lazy import pra evitar dep cíclica (kiosk.store → kioskMode → platform).
    import('~/services/platform/kioskMode')
      .then(({ setNativeKioskLocked }) => setNativeKioskLocked(true))
      .catch((err) =>
        console.warn('[KioskStore] setNativeKioskLocked(true) falhou:', err),
      );
    console.log(
      '[KioskStore] 🔒 lock() completou — isLocked-after:',
      this.isLocked,
    );
  }

  /**
   * Reset do contador de lockout local. Endpoint admin pode disparar isso
   * via comando SSE {@code RESET_KIOSK_LOCKOUT}.
   */
  resetLockout(): void {
    this.resetLockoutState();
  }

  // =========================================================================
  // Audit queue (offline-safe)
  // =========================================================================

  /** Snapshot da fila pendente. UI pode mostrar contador "X eventos não enviados". */
  getPendingAuditEvents(): PendingUnlockAuditEvent[] {
    return [...this.auditQueue];
  }

  /**
   * Marca um conjunto de events como enviados (chamado pelo SyncService após
   * POST com sucesso). Remove apenas os que bateram, mantém os novos.
   */
  markAuditEventsSent(clientEventIds: string[]): void {
    if (clientEventIds.length === 0) return;
    const sent = new Set(clientEventIds);
    this.auditQueue = this.auditQueue.filter((e) => !sent.has(e.clientEventId));
    this.persistAuditQueue();
  }

  // =========================================================================
  // Private helpers
  // =========================================================================

  private async validatePinAgainstHashes(pin: string): Promise<boolean> {
    if (!pin || this.unlockPinHashes.length === 0) return false;
    // bcrypt.compare é async (CPU bound) — checamos um por um pra parar
    // assim que bater. Volume típico é pequeno (1-5 hashes).
    for (const hash of this.unlockPinHashes) {
      try {
        const ok = await bcrypt.compare(pin, hash);
        if (ok) return true;
      } catch (e) {
        console.warn('[KioskStore] bcrypt.compare error (hash inválido?):', e);
      }
    }
    return false;
  }

  /**
   * Pseudocódigo do plano (§6.2):
   *   if (failCount === 10) lockFor(30s);
   *   else if (failCount === 20) lockFor(5min);
   *   else if (failCount > 20 && (failCount - 20) % 20 === 0) lockFor(5min);
   */
  private applyFailureAndComputeLockout(): number {
    const newFailCount = this.failCount + 1;
    let lockoutMs = 0;
    if (newFailCount === FIRST_LOCKOUT_THRESHOLD) {
      lockoutMs = FIRST_LOCKOUT_DURATION_MS;
    } else if (newFailCount === SECOND_LOCKOUT_THRESHOLD) {
      lockoutMs = RECURRING_LOCKOUT_DURATION_MS;
    } else if (
      newFailCount > SECOND_LOCKOUT_THRESHOLD &&
      (newFailCount - SECOND_LOCKOUT_THRESHOLD) % SECOND_LOCKOUT_THRESHOLD === 0
    ) {
      lockoutMs = RECURRING_LOCKOUT_DURATION_MS;
    }

    runInAction(() => {
      this.failCount = newFailCount;
      this.lockedUntil =
        lockoutMs > 0 ? Date.now() + lockoutMs : this.lockedUntil;
    });

    safeWrite(STORAGE_KEY_FAIL_COUNT, String(newFailCount));
    if (lockoutMs > 0 && this.lockedUntil) {
      safeWrite(STORAGE_KEY_LOCKED_UNTIL, String(this.lockedUntil));
    }
    return lockoutMs;
  }

  private resetLockoutState(): void {
    this.failCount = 0;
    this.lockedUntil = null;
    safeWrite(STORAGE_KEY_FAIL_COUNT, '0');
    safeWrite(STORAGE_KEY_LOCKED_UNTIL, null);
  }

  private enqueueAuditEvent(
    event: Omit<PendingUnlockAuditEvent, 'clientEventId' | 'timestamp'>,
  ): void {
    const full: PendingUnlockAuditEvent = {
      ...event,
      clientEventId: generateClientEventId(),
      timestamp: Date.now(),
    };
    runInAction(() => {
      this.auditQueue = [...this.auditQueue, full];
    });
    this.persistAuditQueue();
  }

  private persistAuditQueue(): void {
    safeWrite(STORAGE_KEY_AUDIT_QUEUE, JSON.stringify(this.auditQueue));
  }
}

export const kioskStore = new KioskStore();
export default KioskStore;
